import { API_ROUTES } from "../configuration/RoutesApi"
import { useLoad } from "../context/useLoadContext"
import { ERequestType } from "../Enum/ERequestType"
import {
  addtionalMemory,
  awardMemory,
  categoryMemory,
  comboMemory,
  companyMemory,
  drinksMemory,
  extraServiceMemory,
  mealsMemory,
  noticeMemory,
  promotionsMemory,
  selectionMemory,
} from "../Objects/Memory/Memory"
import { converteDateToSql, setDelay } from "../Objects/Generics/Helps"
import { GetRequest } from "../services/ApiClientService"
import { useGetAddtional } from "./Addtional/useGetAddtional"
import { useGetAward } from "./Award/useGetAward"
import { useGetCategory } from "./Category/useGetCategory"
import { useGetCompany } from "./Company/useGetCompany"
import { useGetCustomer } from "./Costumer/useGetCustomer"
import { useGetCourier } from "./Courier/useGetCourier"
import { useGetDrinks } from "./Drinks/useGetDrinks"
import { useGetCombo } from "./Combo/useGetCombo"
import { useGetEmployee } from "./Employee/useGetEmployee"
import { useGetExtraService } from "./ExtraService/useGetExtraService"
import { useGetMeals } from "./Meals/useGetMeals"
import { useGetOrder } from "./Order/useGetOrder"
import { useGetPromotions } from "./Promotions/useGetPromotions"
import { useGetTables } from "./Tables/useGetTables"
import { useGetCoupon } from "./Coupon/useGetCoupon"
import { useGetTutorial } from "./Tutorial/useGetTutorial"
import { useGetSelection } from "./Selection/useGetSelection"
import { useGetNotice } from "./Notice/useGetNotice"
import { getRestaurantNow } from "../utils/timeZone"

let memoryLoaded = false

export const useReloadMemory = () => {
  const { enableLoad, disableLoad, enableFlagLoad, disableFlagLoad } = useLoad()

  const { handleGetEmployee } = useGetEmployee()

  const { handleGetAward } = useGetAward()

  const { handleGetCompany } = useGetCompany()

  const { handleGetDrinks } = useGetDrinks()

  const { handleGetCombo } = useGetCombo()

  const { handleGetExtraService } = useGetExtraService()

  const { handleGetMeals } = useGetMeals()

  const { handleGetPromotions } = useGetPromotions()

  const { handleGetAddtional } = useGetAddtional()

  const { handleGetCategory } = useGetCategory()

  const { handleGetCostumer } = useGetCustomer()

  const { handleGetTable } = useGetTables()

  const { handleGetOrder } = useGetOrder()

  const { handleGetCourier } = useGetCourier()

  const { handleGetCoupon } = useGetCoupon()

  const { handleGetTutorial } = useGetTutorial()

  const { handleGetSelection } = useGetSelection()

  const { handleGetNotice } = useGetNotice()

  const handleReloadToMemory = async () => {
    if (memoryLoaded) {
      return
    }

    enableLoad()

    memoryLoaded = true

    await Promise.all([
      handleGetTable(() => {}),
      handleGetCompany(() => {}),
      handleGetEmployee(() => {}),
      handleGetAward(() => {}),
      handleGetPromotions(() => {}),
      handleGetAddtional(() => {}),
      handleGetCategory(() => {}),
      handleGetExtraService(() => {}),
      handleGetDrinks(() => {}),
      handleGetMeals(() => {}),
      handleGetCombo(() => {}),
      handleGetCostumer(() => {}),
      handleGetCourier(() => {}),
      handleGetCoupon(() => {}),
      handleGetTutorial(() => {}),
      handleGetSelection(() => {}),
      handleGetNotice(() => {}),
    ])

    memoryLoaded = false

    disableLoad()
  }

  const handleReloadPublicMemory = async (restaurantSlug: string, onCatalogLoaded?: () => void) => {
    enableLoad()

    const hasCompany = await handleGetCompany(() => {}, API_ROUTES.COMPANY.GET_ALL_PUBLIC(restaurantSlug), false)

    if (!hasCompany) {
      disableLoad()

      return false
    }
    categoryMemory.setList([])
    addtionalMemory.setList([])
    drinksMemory.setList([])
    mealsMemory.setList([])
    comboMemory.setList([])
    awardMemory.setList([])
    promotionsMemory.setList([])
    extraServiceMemory.setList([])
    selectionMemory.setList([])
    noticeMemory.setList([])

    const catalogLoading = Promise.all([
      handleGetCategory(() => {}, API_ROUTES.CATEGORY.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetAddtional(() => {}, API_ROUTES.ADDTIONAL.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetDrinks(() => {}, API_ROUTES.DRINK.GET_ALL_PUBLIC(restaurantSlug), false),
      handleGetMeals(() => {}, API_ROUTES.MEAL.GET_ALL_PUBLIC(restaurantSlug), false),
      handleGetCombo(() => {}, API_ROUTES.COMBO.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetAward(() => {}, API_ROUTES.AWARD.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetPromotions(() => {}, API_ROUTES.PROMOTION.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetExtraService(() => {}, API_ROUTES.EXTRA_SERVICE.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetSelection(() => {}, API_ROUTES.SELECTION.GET_ALL_PUBLIC(restaurantSlug)),
      handleGetNotice(() => {}, API_ROUTES.NOTICE.GET_ALL_PUBLIC(restaurantSlug)),
    ])

    disableLoad()

    void catalogLoading.then(() => onCatalogLoaded?.())

    void GetRequest<{ canCreateOrder: boolean }>(
      API_ROUTES.SUBSCRIPTION.PUBLIC_ORDER_AVAILABILITY(restaurantSlug),
    ).then((availabilityResponse) => {
      if (availabilityResponse.success) {
        companyMemory.canCreatePublicOrder = availabilityResponse.data?.canCreateOrder ?? true
      }
    })

    return true
  }

  const handleReloadMealsAndDrinksStock = async () => {
    await Promise.all([handleGetMeals(() => {}, undefined, false), handleGetDrinks(() => {}, undefined, false)])
  }

  const handleReloadPublicOrder = async (restaurantSlug: string) => {
    enableFlagLoad()

    const endDate = getRestaurantNow(companyMemory.timeZoneId)

    const startDate = getRestaurantNow(companyMemory.timeZoneId)

    startDate.setDate(startDate.getDate() - 90)

    await setDelay(500)

    await handleGetOrder(
      () => {},
      converteDateToSql(startDate),
      converteDateToSql(endDate),
      API_ROUTES.ORDER.GET_ALL_PUBLIC(restaurantSlug),
      ERequestType.Customer,
    )

    disableFlagLoad()
  }

  return {
    handleReloadToMemory,
    handleReloadPublicOrder,
    handleReloadPublicMemory,
    handleReloadMealsAndDrinksStock,
  }
}
